import React, { useState, useContext, useEffect } from 'react'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  ButtonsDiv,
  CancelButton,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningText,
} from './style'
import StockContext from '../../../../contexts/StockContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
  StockInterface,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import 'font-awesome/css/font-awesome.min.css'
import StockDropdown from '../StockDropdown'
import ApiService from '../../../../services/ApiService'
import { Info } from 'lucide-react'

const FirstCustomAddPage: React.FC = () => {
  const { stock, setStock, resetContext } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate } =
    useContext(ModalContext)
  const { user } = useContext(UserContext)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})
  const [warningMessage, setWarningMessage] = useState('')
  const [activeMenu, setActiveMenu] = useState(0)

  // Define campos obrigatórios
  const requiredFields = ['material', 'format', 'family', 'density']
  const numberFields = ['density', 'runNumber']

  // IMPORTANTE: O campo 'format' só pode selecionar formatos EXISTENTES
  // A tabela Format agora é global e não permite criação dinâmica de novos formatos
  const fieldsArray: string[][] = [
    ['material', 'heatTreatment', 'format'],
    ['originCountry', 'finishing', 'supplier'],
    ['stockStreet', 'shelf', 'stockLocation'],
    ['density', 'receivedDate', 'heatNumber'],
    ['family', 'fabrication'],
  ]

  const isStringField: boolean[][] = [
    [true, true, true],
    [true, true, true],
    [true, true, true],
    [false, false, false],
    [true, true],
  ]

  // Função para verificar se todos os campos obrigatórios estão preenchidos
  const areRequiredFieldsFilled = () => {
    return requiredFields.every(
      (field) => stock[field as keyof StockInterface] !== '',
    )
  }

  // Verifica os campos obrigatórios no carregamento do modal
  useEffect(() => {
    // Inicia o estado de erros verificando os campos obrigatórios
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    setErrors(newErrors) // Atualiza o estado com erros de campos obrigatórios
    setWarningMessage('') // Limpa a mensagem de advertência
  }, [stock])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (numberFields.includes(name) && isNaN(Number(value))) {
      setWarningMessage(name)
      return
    }
    setWarningMessage('')
    setErrors((prev) => ({
      ...prev,
      [name]: requiredFields.includes(name) && !value,
    }))
    setStock({ ...stock, [name]: value })
  }

  const fetchDensity = async (materialName: string) => {
    try {
      const response = await ApiService.get(`/backend/material/density/${materialName}`)
      const { density } = response.data
      setStock((prevStock) => ({
        ...prevStock,
        density: density.toString(),
      }))
    } catch (error) {
      console.error('Error fetching density:', error)
    }
  }

  const handleDropdownClick = (value: string, field: string) => {
    console.log('🔍 [FIRSTPAGE] Dropdown selecionado:', {
      field,
      oldValue: stock[field as keyof StockInterface],
      newValue: value
    });
    
    setStock({ ...stock, [field]: value })
    setErrors((prev) => ({
      ...prev,
      [field]: requiredFields.includes(field) && !value,
    }))
    setActiveMenu(0)

    // If the material field is changed, fetch its density
    if (field === 'material' && value) {
      console.log('🌡️ [FIRSTPAGE] Material alterado, buscando densidade...');
      fetchDensity(value)
    }
    
    console.log('✅ [FIRSTPAGE] Stock atualizado:', {
      field,
      newStockValue: value,
      totalFilledFields: Object.keys({...stock, [field]: value}).filter(key => stock[key as keyof StockInterface] || (key === field && value)).length
    });
  }

  const nextPage = () => {
    console.log('🚀 [FIRSTPAGE] Iniciando validação para próxima página');
    console.log('📋 [FIRSTPAGE] Estado atual do stock:', {
      material: stock.material,
      format: stock.format,
      family: stock.family,
      density: stock.density,
      totalFields: Object.keys(stock).length,
      filledFields: Object.keys(stock).filter(key => stock[key as keyof StockInterface]).length
    });
    
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    
    console.log('🔍 [FIRSTPAGE] Validação dos campos obrigatórios:', {
      requiredFields,
      errors: newErrors,
      hasErrors: Object.keys(newErrors).length > 0,
      missingFields: Object.keys(newErrors)
    });
    
    setErrors(newErrors)
    
    if (Object.keys(newErrors).length > 0) {
      console.error('❌ [FIRSTPAGE] Validação falhou, permanecendo na primeira página');
      return
    }
    
    console.log('✅ [FIRSTPAGE] Validação passou, navegando para segunda página');
    console.log('🔄 [FIRSTPAGE] Chamando setModalSection("secondCustomAdd")');
    setModalSection('secondCustomAdd')
  }

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>
          {itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'}
        </HeaderText>
        <CloseDiv
          onClick={() => {
            setModalActive(false)
            resetContext()
          }}
        >
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>
      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>
      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => (
            <InputDiv key={colIndex}>
              {activeMenu == rowIndex * 3 + colIndex + 1 &&
              isStringField[rowIndex][colIndex] ? (
                <StockDropdown
                  text={stock[stockField as keyof StockInterface]}
                  field={stockField}
                  onChange={(value) => {
                    handleDropdownClick(value, stockField)
                  }}
                />
              ) : null}

              <InputLabel htmlFor={stockField}>
                {fieldsToHeaders[stockField as keyof FieldsToHeaders]}
                {requiredFields.includes(stockField) && (
                  <Info size={15} style={{marginLeft:5}} color={`${!stock[stockField as keyof StockInterface] ? 'red' : 'gray'}`}/>
                )}
              </InputLabel>
              <Input
                placeholder={stockField === 'format' ? 'Selecione uma opção' : 'Digite aqui'}
                name={stockField}
                value={stock[stockField as keyof StockInterface] || ''}
                readOnly={stockField === 'format'}
                onFocus={() => {
                  setErrors((prev) => ({ ...prev, [stockField]: false }))
                  setActiveMenu(rowIndex * 3 + colIndex + 1)
                }}
                onBlur={() => {
                  setErrors((prev) => ({
                    ...prev,
                    [stockField]:
                      requiredFields.includes(stockField) &&
                      !stock[stockField as keyof StockInterface],
                  }))
                  setActiveMenu(0)
                }}
                onChange={stockField === 'format' ? undefined : handleChange}
                onClick={() => {
                  if (stockField === 'format') {
                    setActiveMenu(rowIndex * 3 + colIndex + 1)
                  }
                }}
                autoComplete='off'
                style={{
                  cursor: stockField === 'format' ? 'pointer' : 'text'
                }}
                /* className={
                  errors[stockField] || (stockField === warningMessage) ? 'redBorder' : ''
                } */
              />
              {warningMessage === stockField && (
                <WarningText>O campo tem que ser um número!</WarningText>
              )}
            </InputDiv>
          ))}
        </InputRowDiv>
      ))}
      <ButtonsDiv>
        <CancelButton onClick={() => setModalSection('addStockMenu')}>
          Cancelar
        </CancelButton>
        <NextButtonDiv
          onClick={nextPage}
          style={{
            pointerEvents: areRequiredFieldsFilled() ? 'auto' : 'none',
            opacity: areRequiredFieldsFilled() ? 1 : 0.5,
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default FirstCustomAddPage
